/** Node/edge facts for the hover debug overlay. Pure; cached per graph. */
type Graph = Record<string, { edges?: Record<string, number> } | undefined>;

const cache = new WeakMap<object, Map<string, number>>();

/** How many words list each word as a neighbour (directional edges). */
export function inDegrees(graph: Graph): Map<string, number> {
  const hit = cache.get(graph);
  if (hit) return hit;
  const m = new Map<string, number>();
  for (const w in graph) {
    for (const n in graph[w]?.edges ?? {}) m.set(n, (m.get(n) ?? 0) + 1);
  }
  cache.set(graph, m);
  return m;
}

export interface NodeDebug {
  word: string;
  inDegree: number;
  outDegree: number;
  /** Similarity of the edge from `from` to this word, if any. */
  similarity: number | null;
  from: string | null;
}

export function nodeDebug(
  graph: Graph | null,
  word: string | null,
  from: string | null = null,
): NodeDebug | null {
  if (!graph || !word || !graph[word]) return null;
  const sim = from ? graph[from]?.edges?.[word] : undefined;
  return {
    word,
    inDegree: inDegrees(graph).get(word) ?? 0,
    outDegree: Object.keys(graph[word]?.edges ?? {}).length,
    similarity: typeof sim === "number" ? sim : null,
    from: typeof sim === "number" ? from : null,
  };
}

export function formatNodeDebug(d: NodeDebug): string {
  const edge =
    d.similarity !== null
      ? ` · ${d.from}→${d.word} sim ${d.similarity.toFixed(3)}`
      : "";
  return `${d.word} · in ${d.inDegree} · out ${d.outDegree}${edge}`;
}
